(function () {
  'use strict';

  /**
   * SkinSaltCart — SKIO Static Build-a-Box cart module.
   *
   * Uses SKIO's get-classic-box-v2 API to fetch box config and flavor mappings.
   * Cart adds a single line item per box with _isClassicBoxV2 + _pvgid:// properties.
   *
   * PACKAGE_DATA is rendered by the VSL picker snippet into window.PACKAGE_DATA (or window.PACKAGE_DATA_2).
   * Format: { '<handle>': { packCount: N, variantId: ID, sellingPlanId: ID } }
   */

  var PACK_COUNT_BY_TIER_INDEX = { 1: 4, 2: 2, 3: 1 };
  var TIER_TO_INDEX = { '3plus1': 1, '2box': 2, '1box': 3 };

  /* ---- SKIO Static Box Config ---- */
  var SKIO_FLAVOR_MAP = {};
  var skioBoxCache = {};

  function fetchSkioBox(variantId, sellingPlanId) {
    var key = variantId + ':' + sellingPlanId;
    if (skioBoxCache[key]) return Promise.resolve(skioBoxCache[key]);
    return fetch('https://api.skio.com/storefront-http/get-classic-box-v2', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        productVariantPlatformId: String(variantId),
        sellingPlanPlatformId: sellingPlanId ? String(sellingPlanId) : undefined
      })
    })
    .then(function (r) { return r.json(); })
    .then(function (resp) {
      if (!resp.ok) throw new Error('SKIO get-classic-box-v2 failed');
      var box = resp.ClassicBoxV2;
      (box.selectableProductVariants || []).forEach(function (v) {
        var pvgid = v.platformId.replace('gid://', '_pvgid://');
        SKIO_FLAVOR_MAP[v.productTitle] = pvgid;
        var parts = v.productTitle.split(' - ');
        if (parts.length > 1) {
          var shortName = parts[parts.length - 1];
          SKIO_FLAVOR_MAP[shortName] = pvgid;
          SKIO_FLAVOR_MAP[shortName.split(' ').sort().join(' ')] = pvgid;
        }
      });
      skioBoxCache[key] = box;
      console.log('[SkinSaltCart] SKIO box loaded:', box.title);
      return box;
    })
    .catch(function (err) {
      console.warn('[SkinSaltCart] SKIO box fetch failed:', err.message);
      return null;
    });
  }

  function isLikelyStagingHost() {
    var h = window.location.hostname.toLowerCase();
    return h.indexOf('skin-salt-staging') !== -1 && h.indexOf('.myshopify.com') !== -1;
  }

  function getPackageData() {
    return window.PACKAGE_DATA || window.PACKAGE_DATA_2 || {};
  }

  function readPackageHandleFromDom(packageTier) {
    var ix = TIER_TO_INDEX[packageTier];
    if (!ix) return null;
    var pickerEl = document.querySelector('[data-product-picker], [data-product-picker-2]');
    if (!pickerEl) return null;
    var card = pickerEl.querySelector('[data-variant-card][data-variant-index="' + ix + '"]');
    if (!card) return null;
    return card.getAttribute('data-variant-card') || null;
  }

  function resolveTierConfig(packageTier) {
    var handle = readPackageHandleFromDom(packageTier);
    if (!handle) return null;
    var pkgData = getPackageData();
    var pkg = pkgData[handle];
    if (!pkg) return null;
    return {
      handle: handle,
      packCount: pkg.packCount,
      variantId: pkg.variantId,
      sellingPlanId: pkg.sellingPlanId,
      label: packageTier
    };
  }

  var AVAILABLE_FLAVORS = [
    'Summer Peach',
    'Passion Fruit Sunrise',
    'Triple Berry Burst',
    'Lemon Lime Spritz'
  ];

  function validateFlavors(flavorSelections, requiredCount) {
    var total = 0;
    for (var flavor in flavorSelections) {
      if (Object.prototype.hasOwnProperty.call(flavorSelections, flavor)) {
        var count = parseInt(flavorSelections[flavor], 10) || 0;
        if (count > 0) total += count;
      }
    }
    return { valid: total === requiredCount, total: total, required: requiredCount };
  }

  /**
   * Build SKIO static box properties — _isClassicBoxV2 + _pvgid:// entries.
   */
  function buildBoxProperties(flavorSelections) {
    var properties = { '_isClassicBoxV2': 'true' };
    for (var flavor in flavorSelections) {
      if (!Object.prototype.hasOwnProperty.call(flavorSelections, flavor)) continue;
      var count = parseInt(flavorSelections[flavor], 10) || 0;
      if (count <= 0) continue;
      properties[flavor] = String(count);
      var pvgid = SKIO_FLAVOR_MAP[flavor] || SKIO_FLAVOR_MAP[flavor.split(' ').sort().join(' ')];
      if (pvgid) properties[pvgid] = String(count);
    }
    return properties;
  }

  function cartFailureMessage(status, data) {
    if (!data || typeof data !== 'object') return 'Cart request failed (HTTP ' + status + ').';
    if (data.description) return String(data.description);
    if (data.message && String(data.message).toLowerCase() !== 'cart error') return String(data.message);
    if (data.errors && typeof data.errors === 'object') {
      var parts = [];
      for (var k in data.errors) {
        if (!Object.prototype.hasOwnProperty.call(data.errors, k)) continue;
        var v = data.errors[k];
        parts.push(k + ': ' + (Array.isArray(v) ? v.join(', ') : String(v)));
      }
      if (parts.length) return parts.join(' ');
    }
    return 'Cart request failed (HTTP ' + status + ').';
  }

  function clearCart() {
    return fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) throw new Error('Could not clear cart.'); });
  }

  function addCartItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(cartFailureMessage(res.status, data));
        var addedItems = data && data.items;
        if (!addedItems || addedItems.length === 0) throw new Error('Items could not be added to cart.');
        return '/checkout';
      });
    });
  }

  function replaceCartWithBox(packageHandle, flavorSelections) {
    var pkgData = getPackageData()[packageHandle];
    if (!pkgData || !pkgData.variantId) {
      return Promise.reject(new Error('Package "' + packageHandle + '" not found.'));
    }

    return fetchSkioBox(pkgData.variantId, pkgData.sellingPlanId).then(function () {
      var properties = buildBoxProperties(flavorSelections);
      var line = { id: pkgData.variantId, quantity: 1, properties: properties };
      if (pkgData.sellingPlanId) line.selling_plan = pkgData.sellingPlanId;

      console.log('[SkinSaltCart] Adding static box:', JSON.stringify(line, null, 2));
      return clearCart().then(function () { return addCartItems([line]); });
    });
  }

  function addAndCheckout(packageTier, flavorSelections, options) {
    options = options || {};
    var config = resolveTierConfig(packageTier);
    if (!config) {
      var msg = 'Invalid package tier: "' + packageTier + '".';
      if (options.onError) options.onError(msg);
      else console.error('[SkinSaltCart]', msg);
      return;
    }

    var validation = validateFlavors(flavorSelections, config.packCount);
    if (options.onValidation) options.onValidation(validation);
    if (!validation.valid) {
      var msg3 = 'Please select exactly ' + config.packCount + ' flavor pack' +
        (config.packCount > 1 ? 's' : '') + '. You selected ' + validation.total + '.';
      if (options.onError) options.onError(msg3);
      else console.error('[SkinSaltCart]', msg3);
      return;
    }

    if (options.onRedirecting) options.onRedirecting();

    replaceCartWithBox(config.handle, flavorSelections)
      .then(function (checkoutPath) { window.location.href = checkoutPath; })
      .catch(function (err) {
        var out = (err && err.message) ? err.message : 'Network error updating cart.';
        if (options.onError) options.onError(out);
        else console.error('[SkinSaltCart]', out);
      });
  }

  function addAndCheckoutWithHandle(packageHandle, flavorSelections, options) {
    options = options || {};
    var pkgData = getPackageData();
    var pkg = pkgData[packageHandle];
    if (!pkg) {
      var msg = 'Package "' + packageHandle + '" not found in PACKAGE_DATA.';
      if (options.onError) options.onError(msg);
      else console.error('[SkinSaltCart]', msg);
      return;
    }

    var validation = validateFlavors(flavorSelections, pkg.packCount);
    if (options.onValidation) options.onValidation(validation);
    if (!validation.valid) {
      var msg3 = 'Please select exactly ' + pkg.packCount + ' flavor pack' +
        (pkg.packCount > 1 ? 's' : '') + '. You selected ' + validation.total + '.';
      if (options.onError) options.onError(msg3);
      else console.error('[SkinSaltCart]', msg3);
      return;
    }

    if (options.onRedirecting) options.onRedirecting();

    replaceCartWithBox(packageHandle, flavorSelections)
      .then(function (checkoutPath) { window.location.href = checkoutPath; })
      .catch(function (err) {
        var out = (err && err.message) ? err.message : 'Network error updating cart.';
        if (options.onError) options.onError(out);
        else console.error('[SkinSaltCart]', out);
      });
  }

  window.SkinSaltCart = {
    addAndCheckout: addAndCheckout,
    addAndCheckoutWithHandle: addAndCheckoutWithHandle,
    validateFlavors: validateFlavors,
    buildBoxProperties: buildBoxProperties,
    fetchSkioBox: fetchSkioBox,
    AVAILABLE_FLAVORS: AVAILABLE_FLAVORS,
    SKIO_FLAVOR_MAP: SKIO_FLAVOR_MAP,
    isLikelyStagingHost: isLikelyStagingHost,
    resolveTierConfig: resolveTierConfig,
    getPackageData: getPackageData
  };
})();


	// set cookie drop button

	let dropDownTimeMs = (typeof DROP_DOWN_TIME_MS !== 'undefined') ? DROP_DOWN_TIME_MS : 1299000;
	let daysToCookieExpires = 30; // cookie stays in browser this many days
	let pageCookieName = "SS-VSL-V1-SHOPIFY";
	if(document.cookie.indexOf(pageCookieName) > -1) {
		// Already visited — show quickly.
		console.log('already visited')
		setTimeout(showDelayed, 5000);
	} else {
		// First visit — use metafield-driven dropdown time.
		console.log('first visit');
		setTimeout(showDelayed, dropDownTimeMs);
		CreateCookie(pageCookieName,"yes");
	}
	function CreateCookie(name, value) {
		let exp   = '';
		let today = new Date();
		let cookieExpirationDate = today.getTime() + (daysToCookieExpires * 24 * 60 * 60 * 1000);
		today.setTime(cookieExpirationDate);
		document.cookie = name + '=' + value + '; path=/; expires=' + today.toUTCString();
	}
	function showDelayed() {
		scrollTo('video')
		$('.next-step-button__container').hide();
		$( ".drop" ).fadeIn(function(){
			pushVslCtaDataLayerEvent();
		}).css("display","block");
	}
	function pushVslCtaDataLayerEvent() {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'vsl-cta-dynamic'
		});
	}

	// set cookie drop button

	function scrollTo(element) {
		let target = document.getElementById(element)
		target.scrollIntoView({behavior: "smooth"});
	}

	// Accordion References

	var acc = document.getElementsByClassName("accordion");
	var i;

	for (i = 0; i < acc.length; i++) {
	  // Skip picker accordions — they have their own handler
	  if (acc[i].hasAttribute('data-accordion')) continue;
	  acc[i].addEventListener("click", function() {
		this.classList.toggle("active");
		var panel = this.nextElementSibling;
		if (panel.style.maxHeight) {
		  panel.style.maxHeight = null;
		} else {
		  panel.style.maxHeight = panel.scrollHeight + "px";
		}
	  });
	}
